import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Search, RefreshCw, Tag, ChevronDown, X, Trash2, PanelLeftClose, PanelLeftOpen, ChevronLeft } from "lucide-react";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast } from "react-toastify";
import ConversationItem from "../components/chats/ConversationItem";
import ChatThread from "../components/chats/ChatThread";
import { getSocket } from "../services/socket";

const TagFilterDropdown = ({ tags, selectedTagId, onSelect, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = tags.find((t) => t.id === selectedTagId);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleDelete = async (e, tag) => {
    e.stopPropagation();
    if (!window.confirm(`Delete tag "${tag.name}"? It will be removed from all customers.`)) return;
    try {
      await api.delete(`/tags/${tag.id}`);
      onDelete(tag.id);
      if (selectedTagId === tag.id) onSelect(null);
      toast.success(`Tag "${tag.name}" deleted`);
    } catch {
      toast.error("Failed to delete tag");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 text-sm border rounded-lg px-3 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          selected ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-white hover:border-gray-400"
        }`}
      >
        <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
        {selected ? (
          <span className="flex-1 flex items-center gap-1.5 text-left truncate">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: selected.color }} />
            <span className="truncate text-gray-700 font-medium">{selected.name}</span>
          </span>
        ) : (
          <span className="flex-1 text-left text-gray-400">Filter by tag...</span>
        )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected && (
            <span
              onClick={(e) => { e.stopPropagation(); onSelect(null); }}
              className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {tags.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No tags yet</p>
          ) : (
            <div className="max-h-52 overflow-y-auto">
              {/* "All" option */}
              <button
                onClick={() => { onSelect(null); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors ${!selectedTagId ? "bg-blue-50 text-blue-600" : "text-gray-600"}`}
              >
                <Tag className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1 text-left">All conversations</span>
              </button>
              <div className="border-t border-gray-100" />
              {tags.map((tag) => {
                const active = selectedTagId === tag.id;
                return (
                  <div
                    key={tag.id}
                    className={`flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors group ${active ? "bg-blue-50" : ""}`}
                  >
                    <button
                      className="flex items-center gap-2 flex-1 text-left"
                      onClick={() => { onSelect(active ? null : tag.id); setOpen(false); }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                      <span className={`text-sm truncate ${active ? "text-blue-600 font-medium" : "text-gray-700"}`}>
                        {tag.name}
                      </span>
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, tag)}
                      className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 text-gray-400 transition-all flex-shrink-0"
                      title={`Delete "${tag.name}"`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Chats = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeConv, setActiveConv] = useState(null);
  const [showThread, setShowThread] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const r = await api.get("/conversations");
      setConversations(r.data.data);
    } catch {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, []);

  // Defined early so the auto-open effect below can use it
  const handleSelectConv = useCallback((conv) => {
    setActiveConv(conv);
    setShowThread(true);
    setConversations((prev) =>
      prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0 } : c))
    );
  }, []);

  // Auto-open a specific conversation when navigated from Broadcast contacts panel
  useEffect(() => {
    const phone = location.state?.openPhone;
    if (!phone || conversations.length === 0) return;

    // Normalize: strip non-digits for comparison (handles +91… vs 91… differences)
    const normalize = (p) => (p || "").replace(/\D/g, "");
    const target = normalize(phone);

    const match = conversations.find((c) =>
      normalize(c.waId) === target ||
      normalize(c.customer?.phoneNumber) === target
    );

    if (match) {
      handleSelectConv(match);
      window.history.replaceState({}, "");
    }
  }, [conversations, location.state?.openPhone, handleSelectConv]);

  useEffect(() => {
    fetchConversations();
    api.get("/tags").then((r) => setTags(r.data.data)).catch(() => {});

    const socket = getSocket();
    socket.on("conversation_update", (update) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === update.id ? { ...c, ...update } : c))
      );
    });

    socket.on("customer_status_changed", ({ customerId, status }) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.customer?.id === customerId
            ? { ...c, customer: { ...c.customer, status } }
            : c
        )
      );
    });

    return () => {
      socket.off("conversation_update");
      socket.off("customer_status_changed");
    };
  }, [fetchConversations]);

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    [conversations]
  );

  useEffect(() => {
    window.__chatsUnread = totalUnread;
    window.dispatchEvent(new Event("chats_unread_updated"));
  }, [totalUnread]);

  const filtered = useMemo(() => {
    return conversations.filter((c) => {
      if (selectedTagId) {
        const hasTag = c.customer?.tags?.some((t) => t.id === selectedTagId);
        if (!hasTag) return false;
      }
      const q = search.toLowerCase();
      if (!q) return true;
      return c.customer?.name?.toLowerCase().includes(q) || c.waId?.toLowerCase().includes(q);
    });
  }, [conversations, search, selectedTagId]);

  const handleStatusChange = (convId, status) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === convId ? { ...c, status } : c))
    );
    if (activeConv?.id === convId) {
      setActiveConv((prev) => ({ ...prev, status }));
    }
  };

  const handleContactSaved = (convId, customer) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === convId ? { ...c, customer } : c))
    );
    if (activeConv?.id === convId) {
      setActiveConv((prev) => ({ ...prev, customer }));
    }
  };

  const selectedTag = tags.find((t) => t.id === selectedTagId);

  return (
    <div className="flex h-[calc(100vh-56px)] bg-gray-50 overflow-hidden">
      {/* Conversation List */}
      <div
        className={`flex flex-col bg-white border-r border-gray-200 flex-shrink-0 transition-all duration-300 ease-in-out ${
          showThread ? "hidden md:flex" : "flex"
        } ${listCollapsed ? "md:w-14 w-14" : "w-full md:w-80"}`}
      >
        {/* Header */}
        <div className={`border-b border-gray-200 flex-shrink-0 ${listCollapsed ? "px-2 py-3" : "px-4 py-4"}`}>
          <div className="flex items-center justify-between mb-0">
            {!listCollapsed && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => navigate(-1)}
                  className="p-1 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                  title="Go back"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <h1 className="text-lg font-bold text-gray-900">Chats</h1>
              </div>
            )}
            <div className={`flex items-center gap-1 ${listCollapsed ? "flex-col w-full" : ""}`}>
              {!listCollapsed && (
                <button
                  onClick={fetchConversations}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setListCollapsed((v) => !v)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                title={listCollapsed ? "Expand" : "Collapse"}
              >
                {listCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Search + tag filter — only when expanded */}
          {!listCollapsed && (
            <div className="mt-3">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <TagFilterDropdown
                tags={tags}
                selectedTagId={selectedTagId}
                onSelect={setSelectedTagId}
                onDelete={(tagId) => setTags((prev) => prev.filter((t) => t.id !== tagId))}
              />
            </div>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              {!listCollapsed && <LoadingSpinner size="sm" text="Loading..." />}
              {listCollapsed && <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />}
            </div>
          ) : filtered.length === 0 && !listCollapsed ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <MessageCircle className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">
                {selectedTagId ? `No chats with tag "${selectedTag?.name}"` : search ? "No results found" : "No conversations yet"}
              </p>
            </div>
          ) : listCollapsed ? (
            /* Collapsed — show avatar circles with unread badge */
            <div className="flex flex-col items-center gap-1 py-2">
              {filtered.map((conv) => {
                const name = conv.customer?.name || conv.waId;
                const initials = name.slice(0, 2).toUpperCase();
                const isActive = activeConv?.id === conv.id;
                return (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConv(conv)}
                    title={name}
                    className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white transition-all ${
                      isActive
                        ? "ring-2 ring-blue-500 ring-offset-1"
                        : "hover:ring-2 hover:ring-gray-300 hover:ring-offset-1"
                    } bg-gradient-to-br from-blue-500 to-blue-600`}
                  >
                    {initials}
                    {conv.unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-white">
                        {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            filtered.map((conv) => (
              <ConversationItem
                key={conv.id}
                conv={conv}
                isActive={activeConv?.id === conv.id}
                onClick={() => handleSelectConv(conv)}
              />
            ))
          )}
        </div>
      </div>

      {/* Chat Thread */}
      <div className={`flex-1 flex flex-col min-w-0 ${showThread ? "flex" : "hidden md:flex"}`}>
        {activeConv ? (
          <ChatThread
            key={activeConv.id}
            conv={activeConv}
            onBack={() => setShowThread(false)}
            onStatusChange={handleStatusChange}
            onContactSaved={handleContactSaved}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50">
            <MessageCircle className="w-16 h-16 mb-4 opacity-20" />
            <p className="text-lg font-medium text-gray-500">Select a conversation</p>
            <p className="text-sm text-gray-400 mt-1">Choose a conversation from the list to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chats;
